import { Extension } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { rowHasHeaderCell } from '@/editor/investmentTableRow';
import { isQuoteWorkspacePreview } from '@/editor/quoteWorkspace';
import { useInvestmentInspectorStore } from '@/store/investmentInspectorStore';
import {
  outdatedRowVersion,
  useProductVersionStore,
  type OutdatedRowVersion,
} from '@/store/productVersionStore';

const pluginKey = new PluginKey<DecorationSet>('investmentRowVersion');

/** lucide triangle-alert */
const ICON_TRIANGLE =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>';

/**
 * Einde van de omschrijving in de eerste cel: het laatste tekstblok met inhoud.
 * Een lege slotalinea wordt overgeslagen, anders staat het icoon op een eigen regel.
 */
function titleEndPos(rowPos: number, row: PMNode): number | null {
  const cell = row.firstChild;
  if (!cell) return null;
  const contentStart = rowPos + 2;
  let lastFilled: number | null = null;
  let last: number | null = null;
  cell.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    const end = contentStart + pos + node.nodeSize - 1;
    last = end;
    if (node.content.size > 0) lastFilled = end;
    return false;
  });
  return lastFilled ?? last;
}

function createFlag(
  view: EditorView,
  getPos: () => number | undefined,
  version: OutdatedRowVersion
): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'investment-row-version-flag-wrap';
  wrap.setAttribute('contenteditable', 'false');

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'investment-row-version-flag';
  const from = version.usedVersion != null ? `v${version.usedVersion} → ` : '';
  const label = `Nieuwere productversie (${from}v${version.version}). Klik om de prijs bij te werken of de melding te negeren.`;
  button.title = label;
  button.setAttribute('aria-label', label);
  button.innerHTML = ICON_TRIANGLE;

  button.addEventListener('mousedown', (event) => {
    event.preventDefault();
    event.stopPropagation();
  });
  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const pos = getPos();
    if (pos == null || pos > view.state.doc.content.size) return;
    // Cursor in deze regel: het paneel ernaast toont dan bijwerken en negeren.
    view.dom.dataset.workspaceSelection = 'active';
    view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, pos)));
    view.focus();
    useInvestmentInspectorStore.getState().requestReveal();
  });

  wrap.appendChild(button);
  return wrap;
}

function buildDecorations(doc: PMNode): DecorationSet {
  const versions = useProductVersionStore.getState();
  if (Object.keys(versions.latest).length === 0) return DecorationSet.empty;

  const decorations: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name !== 'investmentGroupNode') return true;

    node.descendants((child, offset) => {
      if (child.type.name === 'table') return true;
      if (child.type.name !== 'tableRow' || rowHasHeaderCell(child) || child.attrs.investmentProduct || child.attrs.productPrice) return false;

      const version = outdatedRowVersion(
        versions,
        child.attrs.productId as string | null,
        child.attrs.productVersionId as string | null
      );
      if (!version) return false;

      const at = titleEndPos(pos + 1 + offset, child);
      if (at == null) return false;
      decorations.push(
        Decoration.widget(at, (view, getPos) => createFlag(view, getPos, version), {
          side: 1,
          ignoreSelection: true,
          key: `inv-row-version:${child.attrs.productVersionId}:${version.versionId}`,
          stopEvent: (event) =>
            event.type === 'mousedown' || event.type === 'click' || event.type === 'pointerdown',
        })
      );
      return false;
    });
    return false;
  });

  return DecorationSet.create(doc, decorations);
}

/**
 * Waarschuwingsicoon achter de omschrijving van een productregel die op een
 * oudere productversie staat.
 *
 * Alleen in de editor: het is een decoratie, dus het zit niet in het document en
 * niet in de pdf. Klikken zet de cursor in de regel; het investeringspaneel toont
 * dan wat er verandert, met bijwerken en negeren.
 */
export const InvestmentRowVersion = Extension.create({
  name: 'investmentRowVersion',

  addProseMirrorPlugins() {
    const editor = this.editor;

    return [
      new Plugin<DecorationSet>({
        key: pluginKey,
        state: {
          init: (_, state) => buildDecorations(state.doc),
          apply: (tr, current, _oldState, newState) =>
            tr.docChanged || tr.getMeta(pluginKey) ? buildDecorations(newState.doc) : current,
        },
        view: (view) => {
          let destroyed = false;
          // Nieuwe versie-informatie van de server: iconen opnieuw bepalen.
          const unsubscribe = useProductVersionStore.subscribe((state, previous) => {
            if (destroyed) return;
            if (
              state.latest === previous.latest &&
              state.versionNumbers === previous.versionNumbers
            ) {
              return;
            }
            view.dispatch(view.state.tr.setMeta(pluginKey, true).setMeta('addToHistory', false));
          });
          return {
            destroy: () => {
              destroyed = true;
              unsubscribe();
            },
          };
        },
        props: {
          decorations: (state) =>
            editor.isEditable || (editor.isInitialized && isQuoteWorkspacePreview(editor))
              ? (pluginKey.getState(state) ?? DecorationSet.empty)
              : DecorationSet.empty,
        },
      }),
    ];
  },
});

export default InvestmentRowVersion;
